import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { MessageSquare, Loader2, RefreshCw, Zap, Clock, Target } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface TopicStats {
  topic: string;
  asked: number;
  responded: number;
  avgRating: number;
}

interface AIQuestionsStatsCardProps {
  businessId: string;
}

export const AIQuestionsStatsCard: React.FC<AIQuestionsStatsCardProps> = ({ businessId }) => {
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30');
  const [stats, setStats] = useState<{
    totalQuestions: number;
    totalResponses: number;
    responseRate: number;
    avgGenerationTime: number;
    byTopic: TopicStats[];
  }>({
    totalQuestions: 0,
    totalResponses: 0,
    responseRate: 0,
    avgGenerationTime: 0,
    byTopic: [],
  });

  const fetchStats = async () => {
    setLoading(true);
    try {
      const lookbackDate = new Date();
      lookbackDate.setDate(lookbackDate.getDate() - parseInt(timeRange));

      const { data, error } = await supabase
        .from('ai_generated_questions')
        .select('id, question_topic, response_text, response_rating, generation_time_ms, generated_at')
        .eq('business_id', businessId)
        .gte('generated_at', lookbackDate.toISOString());

      if (error) throw error;

      const questions = data || [];
      const total = questions.length;
      const responded = questions.filter(q => q.response_text).length;
      const responseRate = total > 0 ? (responded / total) * 100 : 0;
      const avgGenTime = total > 0
        ? questions.reduce((sum, q) => sum + (q.generation_time_ms || 0), 0) / total
        : 0;

      // Group by topic
      const topicMap: Record<string, { asked: number; responded: number; ratings: number[]; }> = {};
      questions.forEach(q => {
        const topic = q.question_topic || 'general';
        if (!topicMap[topic]) {
          topicMap[topic] = { asked: 0, responded: 0, ratings: [] };
        }
        topicMap[topic].asked++;
        if (q.response_text) {
          topicMap[topic].responded++;
          if (q.response_rating) {
            topicMap[topic].ratings.push(q.response_rating);
          }
        }
      });

      const byTopic = Object.entries(topicMap)
        .map(([topic, data]) => ({
          topic,
          asked: data.asked,
          responded: data.responded,
          avgRating: data.ratings.length > 0
            ? data.ratings.reduce((a, b) => a + b, 0) / data.ratings.length
            : 0,
        }))
        .sort((a, b) => b.asked - a.asked);

      setStats({
        totalQuestions: total,
        totalResponses: responded,
        responseRate,
        avgGenerationTime: avgGenTime,
        byTopic,
      });
    } catch (err) {
      console.error('Error fetching AI question stats:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [businessId, timeRange]);

  const formatTopic = (topic: string) => {
    return topic
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5 text-blue-500" />
          <CardTitle className="text-sm font-medium">AI Questions Performance</CardTitle>
        </div>
        <div className="flex items-center gap-2">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-32 h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="14">Last 14 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="ghost" size="sm" onClick={fetchStats} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : stats.totalQuestions === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No AI questions generated yet</p>
            <p className="text-sm">Enable AI mode in your review forms to start</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Key Metrics */}
            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-center">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <MessageSquare className="h-4 w-4 text-blue-500" />
                </div>
                <div className="text-2xl font-bold text-blue-600">{stats.totalQuestions}</div>
                <div className="text-xs text-blue-700 dark:text-blue-400">Questions Asked</div>
              </div>
              <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg text-center">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <Target className="h-4 w-4 text-green-500" />
                </div>
                <div className="text-2xl font-bold text-green-600">{stats.responseRate.toFixed(1)}%</div>
                <div className="text-xs text-green-700 dark:text-green-400">Response Rate</div>
              </div>
              <div className="p-4 bg-primary/5 rounded-lg text-center">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <Zap className="h-4 w-4 text-primary" />
                </div>
                <div className="text-2xl font-bold text-primary">{Math.round(stats.avgGenerationTime)}ms</div>
                <div className="text-xs text-muted-foreground">Avg Gen Time</div>
              </div>
            </div>

            {/* Response Progress */}
            <div className="p-4 bg-muted/50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Responses Collected</span>
                <span className="text-sm text-muted-foreground">
                  {stats.totalResponses} / {stats.totalQuestions}
                </span>
              </div>
              <Progress value={stats.responseRate} className="h-2" />
            </div>

            {/* Topic Breakdown */}
            {stats.byTopic.length > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-3">Performance by Topic</h4>
                <div className="space-y-3">
                  {stats.byTopic.map((topic) => (
                    <div key={topic.topic} className="p-3 border rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-sm">{formatTopic(topic.topic)}</span>
                        <Badge>
                          {topic.responded}/{topic.asked} responses
                        </Badge>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-xs">
                        <div>
                          <span className="text-muted-foreground">Response Rate</span>
                          <div className="flex items-center gap-2 mt-1">
                            <Progress
                              value={topic.asked > 0 ? (topic.responded / topic.asked) * 100 : 0}
                              className="h-1.5 flex-1"
                            />
                            <span className="font-medium">
                              {topic.asked > 0 ? ((topic.responded / topic.asked) * 100).toFixed(0) : 0}%
                            </span>
                          </div>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Avg Rating</span>
                          <div className="font-medium mt-1">
                            {topic.avgRating > 0 ? topic.avgRating.toFixed(1) : '—'} / 5
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
